/*  ===========================================================
    src/Pages/MarketingTasks.js
    -----------------------------------------------------------
    Shows only outstanding Marketing-department tasks
      • custom “Done” checkbox (gray → green)
      • completed tasks are hidden (no un-hide toggle)
      • “Completed On” column removed
=========================================================== */

import React, { useEffect, useState } from 'react';
import { api } from '../api';

/* ── brand checkbox ───────────────────────────────────────── */
const Checkbox = ({ checked, onChange }) => (
  <label style={{ cursor: 'pointer' }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ display: 'none' }}
    />
    <span
      style={{
        display: 'inline-block',
        width: 24,
        height: 24,
        border: '2px solid',
        borderColor: checked ? '#7eef4f' : '#555',
        background: checked ? '#7eef4f' : 'transparent',
        borderRadius: 4,
        position: 'relative',
        transition: 'all .15s',
      }}
    >
      {checked && (
        <svg
          viewBox="0 0 24 24"
          style={{
            position: 'absolute',
            top: -2,
            left: -2,
            width: 26,
            height: 26,
            fill: 'none',
            stroke: '#1e1e1e',
            strokeWidth: 3,
          }}
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
  </label>
);
/* ─────────────────────────────────────────────────────────── */

const MarketingTasks = () => {
  const [allReleases,     setAllReleases]     = useState([]);
  const [marketingTasks,  setMarketingTasks]  = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);

  /* ─── initial fetch ─── */
  useEffect(() => { fetchAllReleases(); }, []);

  const fetchAllReleases = async () => {
    try {
      setLoading(true); setError(null);
      const res = await api('/api/releases');
      if (!res.ok) throw new Error(`Failed to fetch releases: ${res.status}`);
      const data = await res.json();
      setAllReleases(data);
      buildMarketingTasks(data);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  /* ─── collect Marketing tasks (omit completed) ─── */
  const buildMarketingTasks = releases => {
    const collected = [];
    releases.forEach(rel => {
      const { _id: releaseId, title, coverArt, releaseDate,
              releaseCategories = [], releaseTasks = [] } = rel;

      releaseCategories
        .filter(cat => cat.department === 'Marketing')
        .forEach(cat => {
          releaseTasks
            .filter(t => t.categoryId === cat._id && t.status !== 'Completed') // hide completed
            .forEach(task => collected.push({
              ...task,
              releaseId,
              releaseTitle   : title,
              releaseCoverArt: coverArt || '',
              releaseDate    : releaseDate || null,
              categoryName   : cat.name,
            }));
        });
    });
    setMarketingTasks(collected);
  };

  /* ─── checkbox handler (marks as completed, then disappears) ─── */
  const toggleDone = async (releaseId, task) => {
    try {
      const releaseDoc = allReleases.find(r => String(r._id) === String(releaseId));
      if (!releaseDoc) throw new Error('Release not found');

      const updatedTasks = releaseDoc.releaseTasks.map(t =>
        String(t._id) === String(task._id)
          ? { ...t, status: 'Completed', completedOn: new Date().toISOString() }
          : t
      );

      const resp = await api(`/api/releases/${releaseDoc._id}`, {
        method : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body   : JSON.stringify({ ...releaseDoc, releaseTasks: updatedTasks }),
      });
      if (!resp.ok) throw new Error(`Failed to update tasks: ${resp.status}`);
      const saved = await resp.json();

      const nextAll = allReleases.map(r => (String(r._id) === String(saved._id) ? saved : r));
      setAllReleases(nextAll);
      buildMarketingTasks(nextAll);               // refresh list (task disappears)
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  /* ─── helpers ─── */
  const dueDate = (relDate, dB) => {
    if (!relDate || !dB) return '-';
    const d = new Date(relDate);
    d.setDate(d.getDate() - parseInt(dB, 10));
    return d.toLocaleDateString('en-GB');
  };
  const formatDaysBefore = dB =>
    !dB ? '-' : (+dB === 0 ? 'On Release Day' : `${dB} day${dB > 1 ? 's' : ''} before`);
  const isOverdue = (relDate, dB) => {
    if (!relDate || !dB) return false;
    const d = new Date(relDate);
    d.setDate(d.getDate() - parseInt(dB, 10));
    return d < new Date();
  };

  /* ─── group by category ─── */
  const grouped = marketingTasks.reduce((m, t) => {
    (m[t.categoryName || 'No Category'] ||= []).push(t);
    return m;
  }, {});
  const categoryNames = Object.keys(grouped);

  /* ─── render ─── */
  if (loading)
    return <div style={{ color:'#fff', padding:20 }}>Loading marketing tasks…</div>;
  if (error)
    return <div style={{ color:'#fff', padding:20 }}>Error: {error}</div>;
  if (marketingTasks.length === 0)
    return (
      <div style={{ color:'#fff', padding:20 }}>
        <h1 style={{ margin:'10px 0 30px' }}>Marketing Tasks</h1>
        <p>No pending marketing tasks found in any release.</p>
      </div>
    );

  return (
    <div style={{ padding:20, color:'#fff' }}>
      <h1 style={{ margin:'10px 0 30px' }}>Marketing Tasks</h1>

      {categoryNames.map(cat => (
        <div key={cat} style={{ marginBottom:20, padding:20, background:'#1e1e1e', borderRadius:10 }}>
          <h3 style={{ marginTop:0, marginBottom:15 }}>{cat}</h3>

          <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
            <thead>
              <tr style={{ background:'#333', color:'#fff' }}>
                <th style={{ padding:10, textAlign:'left', width:'30%' }}>Release</th>
                <th style={{ padding:10, textAlign:'left', width:'30%' }}>Task</th>
                <th style={{ padding:10, textAlign:'left', width:'15%' }}>Days Before</th>
                <th style={{ padding:10, textAlign:'left', width:'15%' }}>Due</th>
                <th style={{ padding:10, textAlign:'left', width:'10%' }}>Done</th>
              </tr>
            </thead>
            <tbody>
              {grouped[cat].map(task => {
                const overdue = isOverdue(task.releaseDate, task.daysBefore);
                const dueStr  = dueDate(task.releaseDate, task.daysBefore);

                return (
                  <tr key={task._id} style={{ background:'#1e1e1e', borderBottom:'1px solid #555' }}>
                    {/* release cell */}
                    <td style={{ padding:10 }}>
                      {task.releaseCoverArt && (
                        <img
                          src={task.releaseCoverArt}
                          alt="Cover"
                          style={{
                            width:40, height:40, objectFit:'cover', borderRadius:4,
                            marginRight:10, verticalAlign:'middle',
                          }}
                        />
                      )}
                      <span style={{ verticalAlign:'middle' }}>{task.releaseTitle}</span>
                    </td>

                    {/* task name */}
                    <td style={{ padding:10 }}>
                      <strong>{task.name}</strong>
                    </td>

                    {/* days before */}
                    <td style={{ padding:10 }}>{formatDaysBefore(task.daysBefore)}</td>

                    {/* due */}
                    <td style={{ padding:10 }}>
                      {overdue ? (
                        <span
                          style={{
                            display:'inline-block',
                            padding:'5px 8px',
                            background:'#ff4c4c',
                            borderRadius:5,
                          }}
                        >
                          ⚠ {dueStr}
                        </span>
                      ) : dueStr}
                    </td>

                    {/* checkbox */}
                    <td style={{ padding:10 }}>
                      <Checkbox
                        checked={false}
                        onChange={() => toggleDone(task.releaseId, task)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default MarketingTasks;